import { ErrorHandler, Provider } from '@angular/core';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';

/**
 * Registers the App Insights Angular ErrorHandler so uncaught exceptions are reported — but only when
 * analytics is enabled AND a connection string is present (staging today). Returned as an array so it
 * can be spread into an app's `providers` and contribute nothing when App Insights is off, leaving the
 * default Angular ErrorHandler in place for dev/prod.
 *
 * The `enabled` check must match the gate AnalyticsService uses to create the ApplicationInsights
 * instance. If this registered the ErrorHandler while AnalyticsService did not initialize App
 * Insights, uncaught errors would route to a handler whose `trackException` has no live pipeline — and
 * in dev (enabled: false, connection string present) that mismatch is exactly the condition to avoid.
 *
 * Add to each app's `createAppConfig`: `...provideAppInsights(appSettings)`.
 *
 * The provider only wires the ErrorHandler; the ApplicationInsights instance itself (and the
 * PageView-tracking Angular plugin) is created lazily by AnalyticsService, which reads the same
 * connection string and passes this ErrorHandler into the plugin.
 */
export function provideAppInsights(appSettings: IAppSettings): Provider[] {
	if (!appSettings.analytics?.enabled || !appSettings.analytics?.appInsights?.connectionString) {
		return [];
	}

	return [{ provide: ErrorHandler, useClass: ApplicationinsightsAngularpluginErrorService }];
}
